import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Navbar from "../../components/Navbar";

import image1 from "../../data/obory/vytvarny/11a.jpg";

import artwork1 from "../../data/obory/vytvarny/1.jpg";
import artwork2 from "../../data/obory/vytvarny/10a.jpg";
import artwork3 from "../../data/obory/vytvarny/2a.jpg";
import artwork4 from "../../data/obory/vytvarny/3a.jpg";
import artwork5 from "../../data/obory/vytvarny/4.jpg";
import artwork6 from "../../data/obory/vytvarny/5a.jpg";

// Color variables
const primaryColor = "#d4b682";
const primaryColorLight = "#e0c89a";
const primaryColorLighter = "#ecdaaf";
const textOnPrimary = "#ffffff";

interface SliderImage {
  src: string;
  alt: string;
  link: string;
}

interface GalleryImage {
  src: string;
  alt: string;
}

const sliderImages: SliderImage[] = [
  {
    src: image1,
    alt: "Art exhibition",
    link: "/gallery"
  }
];

const Vytvarni: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  // Gallery images data - moved inside component
  const galleryImages: GalleryImage[] = [
    { src: artwork1, alt: "Žákovská práce 1" },
    { src: artwork2, alt: "Žákovská práce 2" },
    { src: artwork3, alt: "Žákovská práce 3" },
    { src: artwork4, alt: "Žákovská práce 4" },
    { src: artwork5, alt: "Žákovská práce 5" },
    { src: artwork6, alt: "Žákovská práce 6" }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const openImage = (image: GalleryImage) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeImage = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const creativeAreas = [
    { name: "Plošná tvorba", icon: "🎨" },
    { name: "Prostorová tvorba", icon: "🖌️" },
    { name: "Keramika", icon: "🏺" },
    { name: "Grafika", icon: "✏️" },
    { name: "Malba", icon: "🖼️" },
    { name: "Kresba", icon: "📝" }
  ];

  return (
    <div className="min-h-screen bg-[rgba(212,182,130,0.1)] pt-[80px]">
      {/* Navbar */}
      <Navbar />
      
      {/* Full-width Hero Slider */}
      <div className="relative w-screen -ml-[calc(50vw-50%)] h-[90vh]">
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
            <Link to={image.link}>
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <h2 className="text-5xl md:text-7xl font-bold text-white text-center">
                  Výtvarný obor
                </h2>
              </div>
            </Link>
          </div>
        ))}
        
        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Dots indicator */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Main content container */}
      <div className="container mx-auto px-4 sm:px-6 pt-16 pb-12">
        {/* About section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <div className="flex items-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">O výtvarném oboru</h2>
            </div>
            <div className="space-y-4 md:space-y-6 text-base md:text-lg text-gray-700">
              <p className="leading-relaxed">
                V rámci celoročních výtvarných projektů cestujeme časem i dalekými zeměmi, vyprávíme pomocí obrazů příběhy, nebo zkoumáme vlastní sny. Inspirujeme se přístupy různých umělců.
              </p>
              <p className="leading-relaxed">
                Chvíli jsme sochaři, chvíli malíři, chvíli návrháři oděvů, či grafici. Ale hlavně se pomocí různých výtvarných technik snažíme objevit a vyjádřit samy sebe.
              </p>
              <p className="leading-relaxed">
                Kromě praktické tvorby je součástí i výtvarná kultura, která se zaměřuje na dějiny umění, teorii a poznávání různých uměleckých směrů.
              </p>
            </div>
          </div>
        </div>

        {/* Creative Areas section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Výtvarné oblasti</h2>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
              Náš výtvarný obor nabízí rozmanité výtvarné techniky a přístupy.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {creativeAreas.map((area) => (
                <div 
                  key={area.name} 
                  className="flex items-start p-3 md:p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all border border-gray-200 hover:border-gray-300 group"
                >
                  <span className="text-2xl md:text-3xl mr-3 md:mr-4" style={{ color: primaryColor }}>
                    {area.icon}
                  </span>
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-amber-900 transition-colors">
                      {area.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Galerie žákovských prací</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryImages.map((image, index) => (
                <div 
                  key={index} 
                  className="aspect-square rounded-lg overflow-hidden group relative cursor-pointer"
                  onClick={() => openImage(image)}
                >
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="rounded-xl shadow-lg overflow-hidden"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="p-6 md:p-12 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Máte zájem o studium výtvarného oboru?
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              <Link
                to="/chcinazus/prijmacirizeni"
                className="px-6 md:px-8 py-2 md:py-3 bg-white font-medium rounded-lg transition-all transform hover:scale-105"
                style={{ color: primaryColor }}
              >
                Přihláška ke studiu
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4">
          <button 
            onClick={closeImage}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="relative max-w-full max-h-full">
            <img 
              src={selectedImage.src} 
              alt={selectedImage.alt}
              className="max-w-[90vw] max-h-[90vh] object-contain"
            />
          </div>
          
          {/* Navigation arrows for gallery */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = galleryImages.findIndex(img => img.src === selectedImage.src);
              const prevIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
              setSelectedImage(galleryImages[prevIndex]);
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = galleryImages.findIndex(img => img.src === selectedImage.src);
              const nextIndex = (currentIndex + 1) % galleryImages.length;
              setSelectedImage(galleryImages[nextIndex]);
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all"
            aria-label="Next image"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Vytvarni;